import React, {RefObject} from "react";
import Label from "../../../../components/Label/Label";
import Input from "../../../../components/Input/Input";
import {Result, ResultError, Return} from "../../../../lib/result";
import {SectionProps} from "../../../../components/Drawer/ItemDrawer";
import Section from "../../../../components/Drawer/Section/Section";
import LabelsAnnotationsSection, {LabelsAnnotationsSectionProps} from "../../../../components/Drawer/Sections/Metadata/LabelsAnnotationsSection";
import {ManagementV1User} from "../../../../../gen/models/managementV1User";
import {NewResource, Resources} from "../../../../lib/resources";
import client from "../../../../lib/client";
import SectionExpander from "../../../../components/Drawer/SectionExpander/SectionExpander";
import Description from "../../../../components/Description/Description";
import styles from "./UserMetadata.module.scss";

interface MetadataState {
    name: string | undefined;

    username: string | undefined;
    usernameChanged: boolean;

    email: string | undefined;
    emailChanged: boolean;

    displayName: string | undefined;
    displayNameChanged: boolean;

    description: string | undefined;
    descriptionChanged: boolean;

    subject: string | undefined;
    password: string | undefined;
}

interface MetadataProps extends SectionProps, LabelsAnnotationsSectionProps {
    user?: ManagementV1User;
}

export default class UserMetadata extends React.PureComponent<MetadataProps, MetadataState> {
    labelsSectionRef?: RefObject<LabelsAnnotationsSection>;
    state: MetadataState = {
        name: this.props.user ? this.props.user?.metadata?.name : undefined,

        subject: this.props.user?.spec?.subject,

        username: this.props.user ? this.props.user?.spec?.username : undefined,
        usernameChanged: false,

        email: this.props.user ? this.props.user?.spec?.email : undefined,
        emailChanged: false,

        displayName: this.props.user ? this.props.user?.spec?.displayName : undefined,
        displayNameChanged: false,

        description: this.props.user?.spec?.description,
        descriptionChanged: false,

        password: undefined,
    };

    constructor(props: MetadataProps) {
        super(props);

        this.labelsSectionRef = React.createRef<LabelsAnnotationsSection>();
    }

    create = async (user: ManagementV1User): Promise<ResultError> => {
        if (!this.state.username) {
            return Return.Failed("Please enter an username");
        }

        // make sure metadata exists
        if (!user.metadata) {
            user.metadata = {};
        }
        if (!user.spec) {
            user.spec = {};
        }

        const metadata = user.metadata;
        if (!this.state.name) {
            // check if there is a user with the kubernetes username same username
            const result = await client.management(Resources.ManagementV1User).Get(this.state.username);
            if (result.err) {
                metadata.name = this.state.username;
            } else {
                metadata.generateName = this.state.username + "-";
            }
        } else {
            metadata.name = this.state.name;
        }

        user.spec.username = this.state.username;
        user.spec.email = this.state.email;
        user.spec.displayName = this.state.displayName;
        user.spec.subject = this.state.subject || this.state.username;
        user.spec.description = this.state.description;

        return this.labelsSectionRef!.current!.create(metadata);
    };

    update = async (user: ManagementV1User): Promise<ResultError> => {
        if (this.state.usernameChanged) {
            user.spec!.username = this.state.username;
        }
        if (this.state.displayNameChanged) {
            user.spec!.displayName = this.state.displayName;
        }
        if (this.state.emailChanged) {
            user.spec!.email = this.state.email;
        }
        if (this.state.descriptionChanged) {
            user.spec!.description = this.state.description;
        }
        user.spec!.subject = this.state.subject;

        return this.labelsSectionRef!.current!.update(user.metadata!);
    };

    updatePassword = async (user: ManagementV1User): Promise<ResultError> => {
        if (!this.state.password) {
            return Return.Ok();
        }

        const result = await client.management(Resources.ManagementV1UserProfile).Name(user.metadata?.name!).Create(NewResource(Resources.ManagementV1UserProfile, undefined, {
            password: this.state.password
        }));
        if (result.err) {
            return result;
        }

        return Return.Ok();
    };

    batch = (users: ManagementV1User[]): ResultError => {
        return this.labelsSectionRef!.current!.batch(users.map(user => user.metadata!));
    };

    renderUserInfo() {
        return <div>
            <div className={styles["row"]}>
                <div>
                    <Label>Display Name</Label>
                    <Input resetable={this.props.mode === "update"} placeholder={"Firstname Lastname"} onChangedStatus={changed => this.setState({displayNameChanged: changed})} value={this.state.displayName} onChange={(e) => {
                        if (this.props.mode === "create" && this.state.usernameChanged === false) {
                            this.setState({displayName: e.target.value, username: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, "-").replace(/--+/g, "-").replace(/(^[^a-z0-9])|([^a-z0-9]$)/, "")})
                        } else {
                            this.setState({displayName: e.target.value})
                        }
                    }} />
                </div>
                <div>
                    <Label>Kubernetes Name</Label>
                    <Input readOnly={this.props.mode === "update"} placeholder={"Leave empty for auto-generated name"} value={this.state.name} onChange={(e) => this.setState({name: e.target.value})} />
                </div>
            </div>
            <LabelsAnnotationsSection {...this.props} metadata={this.props.user ? this.props.user.metadata : undefined} ref={this.labelsSectionRef} />
            <div className={styles["row"]}>
                <div>
                    <Label>Username</Label>
                    <Input resetable={this.props.mode === "update"} placeholder={"username"} onChangedStatus={changed => this.setState({usernameChanged: changed})} value={this.state.username} onChange={(e) => this.setState({username: e.target.value})} />
                </div>
                <div>
                    <Label>Email Address</Label>
                    <Input resetable={this.props.mode === "update"} placeholder={"email@company.tld"} onChangedStatus={changed => this.setState({emailChanged: changed})} value={this.state.email} onChange={(e) => this.setState({email: e.target.value})} />
                </div>
            </div>
            <SectionExpander name={"Description"}>
                <Label>Description</Label>
                <Input resetable={this.props.mode === "update"}
                       onChangedStatus={changed => this.setState({descriptionChanged: changed})}
                       placeholder={`The user is doing...`}
                       value={this.state.description}
                       onChange={e => this.setState({description: e.target.value})} />
                <Description>A human readable description what the user is used for. Markdown can be used here.</Description>
            </SectionExpander>
            <SectionExpander name={this.props.mode === "update" ? "Change Password" : "Set Password (optional)"}>
                <Label>User password</Label>
                <Input type={"password"} placeholder={"password"} value={this.state.password} onChange={(e) => this.setState({password: e.target.value})} />
                <Description>{this.props.mode === "update" ? "This will overwrite the user's password." : "If you do not set a password, Loft will generate an invite link that you can share with the user, so they can define their own password when they first sign in."}</Description>
            </SectionExpander>
            <SectionExpander name={"Subject (SSO User ID)"}>
                <Label>SSO User ID</Label>
                <Input placeholder={"my-user-in-sso"} value={this.state.subject} onChange={(e) => this.setState({subject: e.target.value})} />
                <Description>SSO User ID is used to map users to a SSO identity. This is useful if you want to switch an existing user from password authentication to SSO authentication.</Description>
            </SectionExpander>
        </div>
    }

    render() {
        return <Section title={`User Details`} noMargin>
            {
                this.props.mode !== "batch" ? this.renderUserInfo() : <LabelsAnnotationsSection {...this.props} metadata={this.props.user ? this.props.user.metadata : undefined} ref={this.labelsSectionRef} />
            }
        </Section>
    }
}